/**
 * Expediente del Cliente — detalle 360 (URL propia: /clientes/expediente/:cedula).
 *
 * La vista por persona que el expediente del préstamo no puede dar: un
 * cliente con varios préstamos, compras de contado y cotizaciones se ve como
 * una sola persona. No existe tabla de clientes en el lado de préstamos — la
 * llave es el documento (cédula/RNC/pasaporte, columna cedula) y el backend
 * normaliza guiones/espacios al comparar (datos históricos del escritorio).
 *
 * Solo lectura (fase 1): cabecera con datos generales e indicadores
 * consolidados + pestañas Préstamos, Compras, Pagos, Cotizaciones, Gestión y
 * Fiador. Cada tabla es un puente: préstamos navega al expediente del
 * préstamo, compras al historial del chasis. Permiso: programa expediente
 * (el mismo de la vista 360 del préstamo); la pestaña Gestión además pide
 * gestioncobros, igual que en el expediente del préstamo.
 */
import React, { useState, useEffect } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import Box from "@mui/material/Box";
import Grid from "@mui/material/Grid";
import Button from "@mui/material/Button";
import Typography from "@mui/material/Typography";
import Stack from "@mui/material/Stack";
import Chip from "@mui/material/Chip";
import Tabs from "@mui/material/Tabs";
import Tab from "@mui/material/Tab";
import CircularProgress from "@mui/material/CircularProgress";
import Collapse from "@mui/material/Collapse";
import IconButton from "@mui/material/IconButton";
import Tooltip from "@mui/material/Tooltip";
import { DataGrid } from "@mui/x-data-grid";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ReceiptLongOutlinedIcon from "@mui/icons-material/ReceiptLongOutlined";
import PaymentsOutlinedIcon from "@mui/icons-material/PaymentsOutlined";
import RequestQuoteOutlinedIcon from "@mui/icons-material/RequestQuoteOutlined";
import SupportAgentOutlinedIcon from "@mui/icons-material/SupportAgentOutlined";
import ForumOutlinedIcon from "@mui/icons-material/ForumOutlined";
import SupervisorAccountOutlinedIcon from "@mui/icons-material/SupervisorAccountOutlined";
import AccountBalanceWalletOutlinedIcon from "@mui/icons-material/AccountBalanceWalletOutlined";
import SavingsOutlinedIcon from "@mui/icons-material/SavingsOutlined";
import ReportProblemOutlinedIcon from "@mui/icons-material/ReportProblemOutlined";
import EventAvailableOutlinedIcon from "@mui/icons-material/EventAvailableOutlined";
import TravelExploreOutlinedIcon from "@mui/icons-material/TravelExploreOutlined";
import PrintOutlinedIcon from "@mui/icons-material/PrintOutlined";
import DescriptionOutlinedIcon from "@mui/icons-material/DescriptionOutlined";
import { useGlobalMDAlert, useMaterialUIController } from "context";
import CorregirCedulaDialog from "components/clientes/CorregirCedulaDialog";
import { FP, PageHeader, StatCard, fpDataGridSx } from "components/financepro";
import { tienePermisosModulo, puedeAccionPrograma } from "lib/seguridad/permisosPorModulo";
import FotoCliente from "components/clientes/FotoCliente";
import { useApi } from "lib/api";
import { aIso, etiquetaGarantia } from "modules/garantias/calendario";
import { estadoPrestamo } from "lib/prestamos";
import { etiquetaDocumento, formatDocumentoAuto } from "lib/documento";
import { imprimirFacturaMotor } from "modules/facturacion/impresionfacturamotor";
import DocumentosVentaContado from "modules/facturacion/documentosventacontado";
import RegistroClienteDialog from "components/clientes/RegistroClienteDialog";
import { CamposExtraDetalle, useFormularioExtra } from "components/camposextra";

const fmtMoney = (v) => {
  const n = typeof v === "number" ? v : parseFloat(String(v || "").replace(/,/g, ""));
  if (Number.isNaN(n)) return "0.00";
  return n.toLocaleString("es-DO", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
};

const toNum = (v) => {
  const n = typeof v === "number" ? v : parseFloat(String(v || "").replace(/,/g, ""));
  return Number.isNaN(n) ? 0 : n;
};

const money = (value) => fmtMoney(value);

const btnBase = { textTransform: "none", borderRadius: 1.8, fontWeight: 700, boxShadow: "none" };
const btnOutline = (c) => ({
  ...btnBase,
  color: c,
  bgcolor: "#FFFFFF",
  border: `1px solid ${c}`,
  "&:hover": { bgcolor: `${c}12`, border: `1px solid ${c}`, color: c },
});
const btnGhost = {
  ...btnBase,
  color: FP.color.textSecondary,
  bgcolor: "transparent",
  "&:hover": { bgcolor: FP.color.surfaceAlt, color: FP.color.textPrimary },
};

const sectionBox = {
  border: `1px solid ${FP.color.border}`,
  borderRadius: 2.5,
  p: { xs: 2, sm: 2.6 },
  mb: 2.4,
  bgcolor: FP.color.cardBg,
  boxShadow: FP.shadow.soft,
};

// Chips de estado compartidos por varias tablas.
const chipActivo = { bgcolor: "#E3F2FD", color: "#1565C0", fontWeight: 700 };
const chipOk = { bgcolor: "#E8F5E9", color: "#2E7D32", fontWeight: 700 };
const chipNeutro = { bgcolor: FP.color.surfaceAlt, color: FP.color.textSecondary, fontWeight: 700 };
const chipPeligro = { bgcolor: FP.color.dangerSoft, color: FP.color.danger, fontWeight: 700 };
const chipAviso = { bgcolor: "#FFF7E6", color: "#B45309", fontWeight: 700 };
const CHIP_TONO_GARANTIA = {
  ok: chipOk,
  aviso: chipAviso,
  peligro: chipPeligro,
  neutro: chipNeutro,
};

const TIPOS_GESTION = [
  { valor: "llamada", label: "Llamada" },
  { valor: "visita", label: "Visita" },
  { valor: "whatsapp", label: "WhatsApp" },
  { valor: "promesa", label: "Promesa de pago" },
  { valor: "nolocalizado", label: "No localizado" },
  { valor: "acuerdo", label: "Acuerdo" },
  { valor: "otro", label: "Otro" },
];
const labelTipoGestion = (v) => TIPOS_GESTION.find((t) => t.valor === v)?.label || v;

function ChipEstadoPrestamo({ status, balance }) {
  const e = estadoPrestamo(status, balance);
  if (e === "activo") return <Chip size="small" label="Activo" sx={chipActivo} />;
  if (e === "saldado") return <Chip size="small" label="Saldado" sx={chipOk} />;
  return <Chip size="small" label="Cancelado" sx={chipNeutro} />;
}

/** Estado vacío de una tabla del expediente: ícono grande + mensaje. */
function VacioTabla({ icono: Icono, texto }) {
  return (
    <Stack alignItems="center" justifyContent="center" spacing={1} sx={{ height: "100%", py: 4 }}>
      <Box
        sx={{
          width: 56,
          height: 56,
          borderRadius: "50%",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          bgcolor: `${FP.color.accentBlue}12`,
          color: FP.color.accentBlue,
        }}
      >
        <Icono sx={{ fontSize: 26 }} />
      </Box>
      <Typography sx={{ fontWeight: 800, color: FP.color.textPrimary }}>
        No hay registros
      </Typography>
      <Typography sx={{ fontSize: "0.82rem", color: FP.color.textSecondary, textAlign: "center" }}>
        {texto}
      </Typography>
    </Stack>
  );
}

/**
 * Card colapsable de una tabla del expediente (mismo patrón del expediente
 * del préstamo). onRowClick opcional: aquí las tablas son puentes a otras
 * pantallas.
 */
function SeccionTabla({ icono: Icono, titulo, descripcion, vacio, rows, columns, onRowClick }) {
  const [abierta, setAbierta] = useState(true);
  return (
    <Box
      sx={{
        border: `1px solid ${FP.color.border}`,
        borderRadius: 2.5,
        mb: 2.4,
        bgcolor: "#FFFFFF",
        boxShadow: FP.shadow.soft,
        overflow: "hidden",
      }}
    >
      <Stack direction="row" alignItems="center" spacing={1.2} sx={{ px: 2.2, py: 1.5 }}>
        <Box
          sx={{
            width: 34,
            height: 34,
            borderRadius: 1.4,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            bgcolor: `${FP.color.navy}10`,
            color: FP.color.navy,
            flexShrink: 0,
          }}
        >
          <Icono sx={{ fontSize: 19 }} />
        </Box>
        <Typography sx={{ fontWeight: 800, fontSize: "0.98rem", color: FP.color.textPrimary }}>
          {titulo}
        </Typography>
        <Box sx={{ flex: 1 }} />
        {descripcion && (
          <Tooltip title={descripcion}>
            <InfoOutlinedIcon sx={{ fontSize: 18, color: FP.color.textMuted }} />
          </Tooltip>
        )}
        <IconButton size="small" onClick={() => setAbierta((a) => !a)}>
          <ExpandMoreIcon
            sx={{
              fontSize: 20,
              color: FP.color.textSecondary,
              transform: abierta ? "rotate(180deg)" : "none",
              transition: "transform .15s ease",
            }}
          />
        </IconButton>
      </Stack>
      <Collapse in={abierta}>
        <Box sx={{ px: 2.2, pb: 2.2 }}>
          <DataGrid
            autoHeight
            rows={rows}
            columns={columns}
            rowHeight={44}
            columnHeaderHeight={44}
            hideFooter={rows.length <= 25}
            pageSizeOptions={[25, 50, 100]}
            initialState={{ pagination: { paginationModel: { pageSize: 25 } } }}
            slots={{ noRowsOverlay: () => <VacioTabla icono={Icono} texto={vacio} /> }}
            onRowClick={onRowClick}
            sx={{
              ...fpDataGridSx,
              "--DataGrid-overlayHeight": "210px",
              border: `1px solid ${FP.color.border}`,
              borderRadius: 2,
              overflow: "hidden",
              ...(onRowClick ? { cursor: "pointer" } : {}),
              "& .MuiDataGrid-columnHeaders": {
                backgroundColor: "#F7F9FC",
                color: FP.color.textPrimary,
                fontSize: "0.84rem",
                fontWeight: 700,
                letterSpacing: 0,
                textTransform: "none",
                borderBottom: `1px solid ${FP.color.border}`,
              },
              "& .MuiDataGrid-columnHeaderTitle": { fontWeight: 700 },
              "& .MuiDataGrid-columnSeparator": { color: FP.color.border },
              "& .MuiDataGrid-columnHeader:focus, & .MuiDataGrid-cell:focus": {
                outline: "none",
              },
              "& .MuiDataGrid-row:last-of-type .MuiDataGrid-cell": {
                borderBottom: "none",
              },
            }}
            disableRowSelectionOnClick
          />
        </Box>
      </Collapse>
    </Box>
  );
}

function Dato({ label, valor, color }) {
  return (
    <Grid item xs={6} sm={4} md={3}>
      <Typography
        sx={{
          fontSize: "0.7rem",
          fontWeight: 700,
          color: FP.color.textMuted,
          textTransform: "uppercase",
        }}
      >
        {label}
      </Typography>
      <Typography
        sx={{
          fontSize: "0.92rem",
          fontWeight: 700,
          color: color || FP.color.textPrimary,
          wordBreak: "break-word",
        }}
      >
        {valor || "—"}
      </Typography>
    </Grid>
  );
}

// Año de la primera relación con el negocio: mínimo entre las fechas
// dd/mm/yyyy de préstamos y compras.
function anioDesde(prestamos, compras) {
  const anios = [...prestamos, ...compras]
    .map((r) => parseInt(String(r.fecha || "").split("/")[2], 10))
    .filter((a) => a > 1900);
  return anios.length ? Math.min(...anios) : null;
}

export default function ClienteDetalle() {
  const { cedula } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { showAlert } = useGlobalMDAlert();
  const { post } = useApi();
  const [controller] = useMaterialUIController();
  const { roleDetalles, roleWeb } = controller;
  // Corregir la cédula en todo el historial: solo AdmG (el backend lo exige).
  const esAdmG = String(roleWeb || "").trim() === "AdmG";
  const [corrigiendoCedula, setCorrigiendoCedula] = useState(false);

  // Volver a la pantalla anterior (expediente del préstamo, buscador...);
  // el buscador solo como respaldo cuando se entra por enlace directo.
  const volver = () => {
    if (location.key !== "default") navigate(-1);
    else navigate("/clientes");
  };

  const [d, setD] = useState(null);
  const [cargando, setCargando] = useState(true);
  const [tab, setTab] = useState(0);
  // Cliente registrado sin factura: editar sus datos mientras no compre.
  const [editandoRegistro, setEditandoRegistro] = useState(false);
  const [recarga, setRecarga] = useState(0);
  const seccionesExtra = useFormularioExtra();

  // Compras al contado: reimprimir la factura y generar sus documentos
  // (acto de venta, descargo...). Los endpoints aceptan el permiso expediente.
  const [ventaDocumentos, setVentaDocumentos] = useState(null);
  const [imprimiendo, setImprimiendo] = useState(false);
  const reimprimirFactura = async (secuencia) => {
    if (imprimiendo) return;
    setImprimiendo(true);
    try {
      const { ok, data } = await post("/api/facturamotor_detalle", { secuencia });
      if (ok) imprimirFacturaMotor(data);
      else showAlert({ message: data?.Error || "No se pudo obtener la factura", color: "error" });
    } finally {
      setImprimiendo(false);
    }
  };

  // La pestaña Gestión pide su propio programa, como en el expediente del
  // préstamo: el endpoint de notas está protegido con gestioncobros.
  const puedeVerGestion = tienePermisosModulo(roleDetalles, "gestioncobros");

  // Reemplazar la foto pide el flag `modificar` del expediente, el mismo
  // que la edición de la venta en la vista 360 del préstamo.
  const puedeEditarExpediente = puedeAccionPrograma(roleDetalles, "expediente", "modificar");
  const [notas, setNotas] = useState([]);

  useEffect(() => {
    let activo = true;
    setCargando(true);
    setD(null);
    post("/api/expedientecliente", { cedula })
      .then(({ ok, data }) => {
        if (!activo) return;
        if (ok) setD(data);
        else
          showAlert({
            message: data.Error || "No se pudo cargar el expediente del cliente",
            color: "error",
          });
      })
      .finally(() => activo && setCargando(false));
    return () => {
      activo = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cedula, recarga]);

  useEffect(() => {
    if (!puedeVerGestion) return undefined;
    let activo = true;
    post("/api/gestioncobros_notascliente", { cedula }).then(({ ok, data }) => {
      if (activo && ok && Array.isArray(data.notas)) setNotas(data.notas);
    });
    return () => {
      activo = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cedula, puedeVerGestion]);

  // Garantía de chasis por factura (módulo Garantías). Con el módulo
  // apagado o sin permiso el endpoint responde error y la columna no sale.
  const puedeVerGarantia =
    tienePermisosModulo(roleDetalles, "garantias") || tienePermisosModulo(roleDetalles, "taller");
  const [garantias, setGarantias] = useState(null); // { [factura]: { texto, tono, chasis } }
  useEffect(() => {
    setGarantias(null);
    if (!puedeVerGarantia || String(cedula || "").replace(/[\s-]/g, "").length < 4) {
      return undefined;
    }
    let activo = true;
    post("/api/garantias_buscar", { texto: cedula }).then(({ ok, data }) => {
      if (!activo || !ok) return;
      const hoy = aIso(new Date());
      const porFactura = {};
      (data.garantias || []).forEach((g) => {
        porFactura[g.factura] = {
          ...etiquetaGarantia(g, data.servicios || [], hoy),
          chasis: g.chasis,
        };
      });
      setGarantias(porFactura);
    });
    return () => {
      activo = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cedula, puedeVerGarantia]);

  if (cargando) {
    return (
      <Box
        sx={{ minHeight: "60vh", display: "flex", alignItems: "center", justifyContent: "center" }}
      >
        <CircularProgress />
      </Box>
    );
  }
  if (!d) {
    return (
      <Box sx={{ p: 4 }}>
        <Button startIcon={<ArrowBackIcon />} onClick={volver} sx={btnGhost}>
          Volver
        </Button>
      </Box>
    );
  }

  const c = d.cliente;
  const nombre = [c.nombres, c.apellidos].filter(Boolean).join(" ").trim();
  const prestamos = d.prestamos || [];
  const compras = d.compras || [];
  const recibos = d.recibos || [];
  const cotizaciones = d.cotizaciones || [];
  const fiador = d.fiador || [];
  // Tratamiento de co-deudor (Configuración): el fiador responde por la
  // deuda, así que sus préstamos garantizados se rotulan "Co-deudor" y el
  // saldo vivo se destaca. Con el flag apagado queda como "Fiador".
  const esCodeudor = !!d.tratamientocodeudor;
  const rolGarante = esCodeudor ? "Co-deudor" : "Fiador";
  const saldoGarantizado = fiador
    .filter((p) => estadoPrestamo(p.status, p.balance) === "activo")
    .reduce((s, p) => s + toNum(p.balance), 0);

  // Indicadores consolidados entre todos los préstamos del cliente.
  const activos = prestamos.filter((p) => estadoPrestamo(p.status, p.balance) === "activo");
  const deudaTotal = activos.reduce((s, p) => s + toNum(p.balance), 0);
  const pagadoTotal = prestamos.reduce((s, p) => s + toNum(p.pagado), 0);
  const atrasoCuotas = activos.reduce((s, p) => s + toNum(p.cuotasatrasadas), 0);
  const montoAtraso = activos.reduce((s, p) => s + toNum(p.montoatraso), 0);
  const ultimoRecibo = recibos[0] || null;
  const desde = anioDesde(prestamos, compras);

  const tablas = [
    {
      titulo: `Préstamos (${prestamos.length})`,
      icono: AccountBalanceWalletOutlinedIcon,
      descripcion:
        "Todos los financiamientos del cliente. Haz clic en uno para abrir su expediente completo.",
      vacio: "Este cliente no tiene financiamientos.",
      rows: prestamos.map((p) => ({ id: p.noprest, ...p })),
      onRowClick: (params) => navigate(`/prestamos/expediente/${params.row.noprest}`),
      columns: [
        { field: "noprest", headerName: "No.", width: 90 },
        { field: "fecha", headerName: "Fecha", width: 110 },
        {
          field: "vehiculo",
          headerName: "Vehículo / garantía",
          flex: 1,
          minWidth: 180,
          valueGetter: (_v, row) =>
            [row.marca, row.modelo].filter(Boolean).join(" ") || row.chasis || "",
        },
        { field: "sucursal", headerName: "Sucursal", width: 130 },
        {
          field: "gestor",
          headerName: "Gestor",
          width: 120,
          valueGetter: (value) => value || "",
        },
        {
          field: "deuda",
          headerName: "Deuda",
          width: 120,
          align: "right",
          headerAlign: "right",
          valueFormatter: money,
        },
        {
          field: "balance",
          headerName: "Balance",
          width: 120,
          align: "right",
          headerAlign: "right",
          valueFormatter: money,
        },
        {
          field: "cuotas",
          headerName: "Cuotas",
          width: 90,
          align: "center",
          headerAlign: "center",
          valueGetter: (_v, row) => `${row.cuotaspagadas}/${row.cuotastotales}`,
        },
        {
          field: "cuotasatrasadas",
          headerName: "Atraso",
          width: 100,
          align: "center",
          headerAlign: "center",
          renderCell: (p) =>
            p.value > 0 ? (
              <Chip size="small" label={`${p.value} cuotas`} sx={chipPeligro} />
            ) : (
              <Chip size="small" label="Al día" sx={chipOk} />
            ),
        },
        {
          field: "status",
          headerName: "Estado",
          width: 110,
          renderCell: (p) => <ChipEstadoPrestamo status={p.value} balance={p.row.balance} />,
        },
      ],
    },
    {
      titulo: `Compras (${compras.length})`,
      icono: ReceiptLongOutlinedIcon,
      descripcion:
        "Facturas del cliente (contado, financiadas y refinanciamientos). Haz clic en una para ver el historial del chasis; en las de contado puedes reimprimir la factura o generar sus documentos. La garantía de chasis abre su garantía.",
      vacio: "Este cliente no tiene facturas registradas.",
      rows: compras.map((x) => ({ id: x.secuencia, ...x })),
      onRowClick: (params) => {
        const ch = String(params.row.chasis || "").trim();
        if (ch) navigate(`/inventario/historialchasis/${encodeURIComponent(ch)}`);
      },
      columns: [
        { field: "secuencia", headerName: "Factura", width: 100 },
        { field: "fecha", headerName: "Fecha", width: 110 },
        {
          field: "vehiculo",
          headerName: "Vehículo",
          flex: 1,
          minWidth: 170,
          valueGetter: (_v, row) => [row.marca, row.modelo].filter(Boolean).join(" "),
        },
        { field: "chasis", headerName: "Chasis", width: 170 },
        ...(garantias
          ? [
              {
                field: "garantia",
                headerName: "Garantía de chasis",
                width: 230,
                sortable: false,
                // Enlace al módulo con el chasis buscado; stopPropagation para
                // que el clic no abra el historial del chasis.
                renderCell: ({ row }) => {
                  const g = garantias[row.secuencia];
                  if (!g) return <Typography sx={{ color: FP.color.textMuted }}>—</Typography>;
                  return (
                    <Tooltip title={`${g.texto} — ver en Garantías`}>
                      <Chip
                        size="small"
                        clickable
                        label={g.texto}
                        sx={{ ...CHIP_TONO_GARANTIA[g.tono], maxWidth: "100%" }}
                        onClick={(e) => {
                          e.stopPropagation();
                          navigate(`/garantias?chasis=${encodeURIComponent(g.chasis)}`);
                        }}
                      />
                    </Tooltip>
                  );
                },
              },
            ]
          : []),
        {
          field: "tipo",
          headerName: "Tipo",
          width: 140,
          renderCell: (p) =>
            p.value === "Contado" ? (
              <Chip size="small" label="Contado" sx={chipOk} />
            ) : p.value === "Refinanciamiento" ? (
              <Chip size="small" label="Refinanc." sx={chipAviso} />
            ) : (
              <Chip size="small" label="Financiada" sx={chipActivo} />
            ),
        },
        {
          field: "precio",
          headerName: "Precio",
          width: 120,
          align: "right",
          headerAlign: "right",
          valueFormatter: money,
        },
        {
          field: "inicial",
          headerName: "Inicial",
          width: 110,
          align: "right",
          headerAlign: "right",
          valueFormatter: money,
        },
        { field: "noprest", headerName: "Préstamo", width: 100 },
        { field: "vendedor", headerName: "Vendedor", width: 130 },
        {
          field: "acciones",
          headerName: "",
          width: 96,
          sortable: false,
          filterable: false,
          align: "center",
          // Solo en ventas al contado: las financiadas tienen su factura y
          // sus documentos en el expediente del préstamo. stopPropagation
          // para que el clic no abra el historial del chasis.
          renderCell: ({ row }) =>
            row.tipo === "Contado" ? (
              <Stack direction="row" spacing={0.5} onClick={(e) => e.stopPropagation()}>
                <Tooltip title="Reimprimir factura">
                  <IconButton
                    size="small"
                    color="primary"
                    onClick={() => reimprimirFactura(row.secuencia)}
                  >
                    <PrintOutlinedIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
                <Tooltip title="Documentos de la venta (acto de venta...)">
                  <IconButton
                    size="small"
                    color="primary"
                    onClick={() =>
                      setVentaDocumentos({ secuencia: row.secuencia, nombre, chasis: row.chasis })
                    }
                  >
                    <DescriptionOutlinedIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              </Stack>
            ) : null,
        },
      ],
    },
    {
      titulo: `Pagos (${recibos.length})`,
      icono: PaymentsOutlinedIcon,
      descripcion:
        "Recibos de pago de todos los préstamos del cliente, del más reciente al más viejo.",
      vacio: "Este cliente no tiene pagos registrados.",
      rows: recibos.map((x, i) => ({ id: i, ...x })),
      columns: [
        { field: "norecibo", headerName: "Recibo", width: 110 },
        { field: "noprest", headerName: "Préstamo", width: 100 },
        { field: "fecha", headerName: "Fecha", width: 110 },
        {
          field: "vpagcap",
          headerName: "Capital",
          width: 120,
          align: "right",
          headerAlign: "right",
          valueFormatter: money,
        },
        {
          field: "vpagint",
          headerName: "Interés",
          width: 120,
          align: "right",
          headerAlign: "right",
          valueFormatter: money,
        },
        {
          field: "vpagmora",
          headerName: "Mora",
          width: 110,
          align: "right",
          headerAlign: "right",
          valueFormatter: money,
        },
        {
          field: "total",
          headerName: "Total",
          flex: 1,
          minWidth: 120,
          align: "right",
          headerAlign: "right",
          valueFormatter: money,
        },
      ],
    },
    {
      titulo: `Cotizaciones (${cotizaciones.length})`,
      icono: RequestQuoteOutlinedIcon,
      descripcion:
        "Interés del cliente que aún no cierra: cotizaciones a su documento, vigentes y vencidas.",
      vacio: "Este cliente no tiene cotizaciones.",
      rows: cotizaciones.map((x) => ({ ...x })),
      columns: [
        { field: "id", headerName: "No.", width: 80 },
        { field: "fecha", headerName: "Fecha", width: 110 },
        {
          field: "vehiculo",
          headerName: "Vehículo",
          flex: 1,
          minWidth: 170,
          valueGetter: (_v, row) => [row.marca, row.modelo].filter(Boolean).join(" "),
        },
        {
          field: "precio",
          headerName: "Precio",
          width: 120,
          align: "right",
          headerAlign: "right",
          valueFormatter: money,
        },
        {
          field: "inicial",
          headerName: "Inicial",
          width: 110,
          align: "right",
          headerAlign: "right",
          valueFormatter: money,
        },
        {
          field: "concredito",
          headerName: "Condición",
          width: 110,
          renderCell: (p) =>
            p.value === "S" ? (
              <Chip size="small" label="Crédito" sx={chipActivo} />
            ) : (
              <Chip size="small" label="Contado" sx={chipNeutro} />
            ),
        },
        {
          field: "vigente",
          headerName: "Estado",
          width: 110,
          renderCell: (p) =>
            p.row.status !== "A" ? (
              <Chip size="small" label="Anulada" sx={chipNeutro} />
            ) : p.value ? (
              <Chip size="small" label="Vigente" sx={chipOk} />
            ) : (
              <Chip size="small" label="Vencida" sx={chipAviso} />
            ),
        },
        { field: "vendedor", headerName: "Vendedor", width: 130 },
      ],
    },
    ...(puedeVerGestion
      ? [
          {
            titulo: `Gestión (${notas.length})`,
            icono: SupportAgentOutlinedIcon,
            iconoTabla: ForumOutlinedIcon,
            descripcion:
              "Notas de gestión de cobro de todos los préstamos del cliente, en una sola línea de tiempo. Se registran desde el expediente de cada préstamo.",
            vacio: "Aún no hay gestiones registradas para este cliente.",
            rows: notas.map((n) => ({ ...n })),
            columns: [
              { field: "fechahora", headerName: "Fecha", width: 160 },
              { field: "noprest", headerName: "Préstamo", width: 100 },
              { field: "usuario", headerName: "Usuario", width: 120 },
              {
                field: "tipo",
                headerName: "Tipo",
                width: 140,
                renderCell: (p) => (
                  <Chip
                    size="small"
                    label={labelTipoGestion(p.value)}
                    sx={p.value === "promesa" ? chipAviso : chipNeutro}
                  />
                ),
              },
              { field: "nota", headerName: "Nota", flex: 1, minWidth: 200 },
              {
                field: "fechapromesa",
                headerName: "Promesa",
                width: 250,
                renderCell: (p) =>
                  p.row.fechapromesa ? (
                    <Stack direction="row" spacing={1} alignItems="center" sx={{ height: "100%" }}>
                      <span>{`${p.row.fechapromesa} · RD$ ${fmtMoney(p.row.montopromesa)}`}</span>
                      {p.row.estadopromesa === "cumplida" ? (
                        <Chip size="small" label="Cumplida" sx={chipOk} />
                      ) : p.row.estadopromesa === "vencida" ? (
                        <Chip size="small" label="Vencida" sx={chipPeligro} />
                      ) : (
                        <Chip size="small" label="Pendiente" sx={chipAviso} />
                      )}
                    </Stack>
                  ) : null,
              },
            ],
          },
        ]
      : []),
    {
      titulo: `${rolGarante} (${fiador.length})`,
      icono: SupervisorAccountOutlinedIcon,
      descripcion: esCodeudor
        ? "Préstamos de otras personas donde este cliente es co-deudor: la deuda también es suya."
        : "Préstamos de otras personas donde este cliente figura como fiador: riesgo indirecto que no se ve en ningún otro informe.",
      vacio: esCodeudor
        ? "Este cliente no es co-deudor en ningún préstamo."
        : "Este cliente no es fiador en ningún préstamo.",
      rows: fiador.map((p) => ({ id: p.noprest, ...p })),
      onRowClick: (params) => navigate(`/prestamos/expediente/${params.row.noprest}`),
      columns: [
        { field: "noprest", headerName: "No.", width: 90 },
        { field: "nombres", headerName: "Deudor", flex: 1, minWidth: 190 },
        {
          field: "cedula",
          headerName: "Documento",
          width: 150,
          valueFormatter: (value) => formatDocumentoAuto(value),
        },
        { field: "fecha", headerName: "Fecha", width: 110 },
        {
          field: "balance",
          headerName: "Balance",
          width: 130,
          align: "right",
          headerAlign: "right",
          valueFormatter: money,
        },
        {
          field: "cuotasatrasadas",
          headerName: "Atraso",
          width: 100,
          align: "center",
          headerAlign: "center",
          renderCell: (p) =>
            p.value > 0 ? (
              <Chip size="small" label={`${p.value} cuotas`} sx={chipPeligro} />
            ) : (
              <Chip size="small" label="Al día" sx={chipOk} />
            ),
        },
        {
          field: "status",
          headerName: "Estado",
          width: 110,
          renderCell: (p) => <ChipEstadoPrestamo status={p.value} balance={p.row.balance} />,
        },
      ],
    },
  ];
  const tablaActual = tablas[Math.min(tab, tablas.length - 1)];

  return (
    <Box component="main" sx={{ minHeight: "100vh", bgcolor: FP.color.surface }}>
      <PageHeader
        title="Expediente del Cliente"
        subtitle={`${nombre || "Sin nombre"} · ${formatDocumentoAuto(c.cedula)}`}
        cta={
          <Stack direction="row" spacing={1.2}>
            {tienePermisosModulo(roleDetalles, "dataorbit") && (
              <Button
                startIcon={<TravelExploreOutlinedIcon />}
                onClick={() => navigate("/dataorbit")}
                sx={btnOutline(FP.color.navy)}
              >
                Consultar en DataOrbit
              </Button>
            )}
            {esAdmG && (
              <Button onClick={() => setCorrigiendoCedula(true)} sx={btnOutline(FP.color.navy)}>
                Corregir cédula
              </Button>
            )}
            <Button startIcon={<ArrowBackIcon />} onClick={volver} sx={btnOutline(FP.color.navy)}>
              Volver
            </Button>
          </Stack>
        }
      />
      {esAdmG && (
        <CorregirCedulaDialog
          open={corrigiendoCedula}
          onClose={() => setCorrigiendoCedula(false)}
          cedula={String(c.cedula || cedula || "")}
          onCorregida={(nueva) => {
            setCorrigiendoCedula(false);
            navigate(`/clientes/expediente/${encodeURIComponent(nueva)}`, { replace: true });
          }}
        />
      )}

      <Box sx={{ p: { xs: 2, md: 3 } }}>
        {/* Datos generales */}
        <Box sx={sectionBox}>
          <Stack direction="row" spacing={2} alignItems="flex-start" sx={{ mb: 2 }}>
            <FotoCliente
              cedula={c.cedula}
              nombre={nombre}
              fotoB64={d.foto_b64}
              puedeEditar={puedeEditarExpediente}
            />
            <Box sx={{ flex: 1 }}>
              <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
                <Typography sx={{ fontWeight: 800, fontSize: "1.05rem" }}>
                  {nombre || "Sin nombre"}
                </Typography>
                {activos.length > 0 && (
                  <Chip
                    size="small"
                    label={`${activos.length} ${
                      activos.length === 1 ? "préstamo activo" : "préstamos activos"
                    }`}
                    sx={chipActivo}
                  />
                )}
                {atrasoCuotas > 0 && (
                  <Chip size="small" label={`${atrasoCuotas} cuotas atrasadas`} sx={chipPeligro} />
                )}
                {desde && <Chip size="small" label={`Cliente desde ${desde}`} sx={chipNeutro} />}
                {d.registrado && !desde && (
                  <Chip
                    size="small"
                    label={`Registrado sin factura${
                      d.registrado.fecha ? ` · ${d.registrado.fecha}` : ""
                    }`}
                    sx={chipNeutro}
                  />
                )}
                {d.registrado?.editable && (
                  <Button
                    size="small"
                    onClick={() => setEditandoRegistro(true)}
                    sx={{ ...btnGhost, py: 0.2, minHeight: 0 }}
                  >
                    Editar datos
                  </Button>
                )}
                {fiador.length > 0 && (
                  <Chip
                    size="small"
                    label={`${rolGarante} en ${fiador.length} ${
                      fiador.length === 1 ? "préstamo" : "préstamos"
                    }${esCodeudor && saldoGarantizado > 0 ? ` · RD$ ${fmtMoney(saldoGarantizado)}` : ""}`}
                    sx={chipAviso}
                  />
                )}
              </Stack>
              <Typography sx={{ fontSize: "0.82rem", color: FP.color.textSecondary }}>
                {[c.direccion, c.urb].filter(Boolean).join(", ")}
                {(c.telefono || c.celular) &&
                  ` · ${[c.telefono, c.celular].filter(Boolean).join(" / ")}`}
              </Typography>
            </Box>
          </Stack>
          <Grid container spacing={2}>
            <Dato label={etiquetaDocumento(c.cedula)} valor={formatDocumentoAuto(c.cedula)} />
            <Dato label="Teléfono" valor={[c.telefono, c.celular].filter(Boolean).join(" / ")} />
            <Dato label="Email" valor={c.email} />
            <Dato label="Apodo" valor={c.apodo} />
            <Dato label="Dirección" valor={[c.direccion, c.urb].filter(Boolean).join(", ")} />
            <Dato label="Comentario" valor={c.comentario} />
            {c.edad != null && c.edad !== "" && <Dato label="Edad" valor={String(c.edad)} />}
          </Grid>
          {d.registrado?.editable && Object.keys(d.registrado.datosextra || {}).length > 0 && (
            <CamposExtraDetalle secciones={seccionesExtra} valores={d.registrado.datosextra} />
          )}
        </Box>

        {/* Indicadores consolidados */}
        <Grid container spacing={2} sx={{ mb: 2.4 }}>
          <Grid item xs={12} sm={6} md={3}>
            <StatCard
              label="Deuda total"
              value={`RD$ ${fmtMoney(deudaTotal)}`}
              icon={AccountBalanceWalletOutlinedIcon}
              accent={FP.color.navy}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <StatCard
              label="Pagado histórico"
              value={`RD$ ${fmtMoney(pagadoTotal)}`}
              icon={SavingsOutlinedIcon}
              accent="#2E7D32"
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <StatCard
              label="Monto en atraso"
              value={`RD$ ${fmtMoney(montoAtraso)}`}
              icon={ReportProblemOutlinedIcon}
              accent={montoAtraso > 0 ? FP.color.danger : FP.color.navy}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <StatCard
              label="Último pago"
              value={
                ultimoRecibo ? `${ultimoRecibo.fecha} · RD$ ${fmtMoney(ultimoRecibo.total)}` : "—"
              }
              icon={EventAvailableOutlinedIcon}
              accent={FP.color.accentBlue}
            />
          </Grid>
        </Grid>

        {/* Pestañas */}
        <Box sx={{ ...sectionBox, mb: 0 }}>
          <Tabs
            value={Math.min(tab, tablas.length - 1)}
            onChange={(_, v) => setTab(v)}
            variant="scrollable"
            allowScrollButtonsMobile
            scrollButtons={false}
            TabIndicatorProps={{ sx: { display: "none" } }}
            sx={{
              mb: 2.4,
              minHeight: 44,
              "& .MuiTabs-flexContainer": { gap: 1, width: "100%" },
              "& .MuiTab-root": { flex: { md: 1 }, maxWidth: "none" },
            }}
          >
            {tablas.map((t) => {
              const Icono = t.icono;
              return (
                <Tab
                  key={t.titulo}
                  icon={Icono ? <Icono sx={{ fontSize: 18 }} /> : undefined}
                  iconPosition="start"
                  label={t.titulo}
                  sx={{
                    textTransform: "none",
                    fontWeight: 700,
                    fontSize: "0.85rem",
                    minHeight: 40,
                    px: 2,
                    borderRadius: 999,
                    border: `1px solid ${FP.color.border}`,
                    color: FP.color.textSecondary,
                    bgcolor: "#FFFFFF",
                    "& .MuiSvgIcon-root": { color: "inherit" },
                    "&:hover": { borderColor: "#0B1220", color: "#0B1220" },
                    // Activo: fondo negro y letra/ícono blancos (el tema de la
                    // plantilla fuerza color con !important, ver
                    // assets/theme/components/tabs/tab.js).
                    "&.MuiTab-root.Mui-selected": {
                      bgcolor: "#0B1220",
                      borderColor: "#0B1220",
                      color: "#FFFFFF !important",
                    },
                    "&.MuiTab-root.Mui-selected .MuiSvgIcon-root": {
                      color: "#FFFFFF !important",
                    },
                  }}
                />
              );
            })}
          </Tabs>

          <SeccionTabla
            icono={tablaActual.iconoTabla || tablaActual.icono}
            titulo={tablaActual.titulo}
            descripcion={tablaActual.descripcion}
            vacio={tablaActual.vacio}
            rows={tablaActual.rows}
            columns={tablaActual.columns}
            onRowClick={tablaActual.onRowClick}
          />
        </Box>
      </Box>

      <DocumentosVentaContado venta={ventaDocumentos} onClose={() => setVentaDocumentos(null)} />
      <RegistroClienteDialog
        open={editandoRegistro}
        cedula={c.cedula}
        onClose={() => setEditandoRegistro(false)}
        onGuardado={() => {
          setEditandoRegistro(false);
          setRecarga((n) => n + 1);
        }}
      />
    </Box>
  );
}
